// b07 Tomosha — vintage-fair variant (refined).
// Maximalist = visual magic + calming ambient sound. The hero keeps the
// rotating ferris wheel, striped tents, popcorn cart and old-poster
// typography. Heavy text-only sections (8 attractions, 5 slogans, ticket
// stamp panel) have been stripped. A shared sine-pad ambient hook drives
// a small floating bottom-right music toggle (D major nostalgic carousel).

import { useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import type { VariantProps } from "./types";
import {
  CountdownSection,
  StorySection,
  GallerySection,
  ScheduleSection,
  MapSection,
  RsvpSection,
  ShareSection,
  GuestFooter,
} from "@/components/sections";
import { useSound } from "@/lib/sound";
import { useAmbient, useReducedMotion } from "./_ambient";
import { MusicToggle } from "@/components/MusicToggle";

// ─── 8-step schedule ─────────────────────────────────────────────────────
// One slot ("{age}-yillik tort kesish") interpolates the parsed age so the
// schedule text matches the centerpiece. Built lazily per render.
const buildSchedule = (age: string): Array<[string, string]> => [
  ["16:00", "Darvozalar ochiladi — bilet, mo'ylov, beretka tarqatish"],
  ["16:30", "Ferris-wheel boshlanadi — birinchi savat ochiladi"],
  ["17:00", "Popkorn aravasi va limonad kioski navbati"],
  ["17:30", "Ko'ngilochar shou — sehrgar Asilbek va aktyorlar"],
  ["18:30", "Dasturxon — jonli kalliope organ ostida"],
  ["19:30", "Raqs — vals, polka va flapper-tarz"],
  ["20:30", `${age}-yillik tort kesish — qiroliyona shamlar`],
  ["22:00", "Darvozalar yopiladi — fonariklar va xayrli kech"],
];

// ─── scroll-reveal hook ──────────────────────────────────────────────────
function useReveal<T extends HTMLElement>(): [React.MutableRefObject<T | null>, boolean] {
  const ref = useRef<T | null>(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof window === "undefined" || !window.IntersectionObserver) {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setShown(true);
            io.disconnect();
            return;
          }
        }
      },
      { threshold: 0.12 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return [ref, shown];
}

function Reveal({ children }: { children: React.ReactNode }) {
  const [ref, shown] = useReveal<HTMLDivElement>();
  return (
    <div
      ref={ref}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? "none" : "translateY(18px)",
        transition: "opacity 700ms ease, transform 700ms cubic-bezier(.2,.8,.2,1)",
      }}
    >
      {children}
    </div>
  );
}

// ─── SVG: rotating ferris wheel ──────────────────────────────────────────
function FerrisWheel({
  crimson,
  ink,
  bg,
  spin,
  size = 320,
}: {
  crimson: string;
  ink: string;
  bg: string;
  spin: boolean;
  size?: number;
}) {
  return (
    <svg
      viewBox="0 0 240 240"
      style={{ width: size, height: size, color: crimson }}
      aria-hidden
    >
      {/* base tower */}
      <path d="M100 240 L 116 130 L 124 130 L 140 240 Z" fill={ink} />
      <rect x="92" y="240" width="56" height="6" fill={ink} />
      {/* rotating group */}
      <g
        style={{
          transformOrigin: "120px 120px",
          transformBox: "fill-box",
          animation: spin ? "vfWheelSpin 50s linear infinite" : "none",
        }}
      >
        <circle cx="120" cy="120" r="92" fill="none" stroke={crimson} strokeWidth="3.6" />
        <circle cx="120" cy="120" r="86" fill="none" stroke={crimson} strokeWidth="0.8" />
        {Array.from({ length: 8 }).map((_, i) => {
          const a = (i * Math.PI) / 4;
          const x = 120 + Math.cos(a) * 92;
          const y = 120 + Math.sin(a) * 92;
          return <line key={i} x1="120" y1="120" x2={x} y2={y} stroke={ink} strokeWidth="1.4" />;
        })}
        {Array.from({ length: 8 }).map((_, i) => {
          const a = (i * Math.PI) / 4;
          const x = 120 + Math.cos(a) * 92;
          const y = 120 + Math.sin(a) * 92;
          return (
            <g key={`g-${i}`}>
              <line x1={x} y1={y} x2={x} y2={y + 8} stroke={ink} strokeWidth="0.9" />
              <rect
                x={x - 8}
                y={y + 8}
                width="16"
                height="12"
                rx="2"
                fill={i % 2 === 0 ? crimson : bg}
                stroke={ink}
                strokeWidth="0.9"
              />
              <line x1={x - 8} y1={y + 14} x2={x + 8} y2={y + 14} stroke={ink} strokeWidth="0.5" />
            </g>
          );
        })}
        <circle cx="120" cy="120" r="14" fill={crimson} />
        <circle cx="120" cy="120" r="6" fill={ink} />
      </g>
      <path d="M120 28 L 120 8 L 144 16 Z" fill={crimson} stroke={ink} strokeWidth="0.6" />
    </svg>
  );
}

// ─── SVG: striped tent ───────────────────────────────────────────────────
function StripedTent({ crimson, ink, bg, size = 240 }: { crimson: string; ink: string; bg: string; size?: number }) {
  return (
    <svg viewBox="0 0 200 240" style={{ width: size, height: (size * 240) / 200 }} aria-hidden>
      <defs>
        <pattern id="vf-tent-stripe" x="0" y="0" width="14" height="14" patternUnits="userSpaceOnUse" patternTransform="rotate(20)">
          <rect x="0" y="0" width="7" height="14" fill={crimson} />
          <rect x="7" y="0" width="7" height="14" fill={bg} />
        </pattern>
      </defs>
      <path
        d="M20 240 L 20 130 C 20 95 70 75 100 60 C 130 75 180 95 180 130 L 180 240 Z"
        fill="url(#vf-tent-stripe)"
        stroke={ink}
        strokeWidth="1.4"
      />
      <path d="M100 60 L 100 22" stroke={ink} strokeWidth="1.6" />
      <path d="M100 12 L 116 22 L 100 30 L 84 22 Z" fill={crimson} stroke={ink} strokeWidth="0.8" />
      <path d="M80 240 C 80 170 120 170 120 240 Z" fill={ink} opacity="0.9" />
      <rect x="60" y="140" width="80" height="18" fill={bg} stroke={ink} strokeWidth="1" />
      <text x="100" y="153" textAnchor="middle" fontFamily="var(--font-ui)" fontSize="9" fontWeight="800" fill={crimson} letterSpacing="2">
        TOMOSHA
      </text>
    </svg>
  );
}

// ─── SVG: popcorn cart ───────────────────────────────────────────────────
function PopcornCart({ crimson, ink, bg, size = 160 }: { crimson: string; ink: string; bg: string; size?: number }) {
  return (
    <svg viewBox="0 0 100 120" style={{ width: size, height: (size * 120) / 100 }} aria-hidden>
      <rect x="22" y="42" width="56" height="42" fill={bg} stroke={ink} strokeWidth="1.4" />
      <path d="M22 42 h56 M34 42 v42 M46 42 v42 M58 42 v42 M70 42 v42" stroke={crimson} strokeWidth="2" />
      <rect x="18" y="34" width="64" height="10" fill={ink} />
      <rect x="22" y="24" width="56" height="12" fill={crimson} />
      <text x="50" y="33" textAnchor="middle" fontFamily="var(--font-ui)" fontSize="7" fontWeight="800" fill={bg} letterSpacing="1">POPCORN</text>
      {/* puffed kernels */}
      <circle cx="32" cy="20" r="4" fill={bg} stroke={ink} strokeWidth="0.8" />
      <circle cx="44" cy="14" r="5" fill={bg} stroke={ink} strokeWidth="0.8" />
      <circle cx="56" cy="16" r="4" fill={bg} stroke={ink} strokeWidth="0.8" />
      <circle cx="66" cy="22" r="3.5" fill={bg} stroke={ink} strokeWidth="0.8" />
      {/* wheels */}
      <circle cx="32" cy="98" r="8" fill={ink} />
      <circle cx="32" cy="98" r="3" fill={bg} />
      <circle cx="68" cy="98" r="8" fill={ink} />
      <circle cx="68" cy="98" r="3" fill={bg} />
      {/* handle */}
      <line x1="78" y1="50" x2="96" y2="40" stroke={ink} strokeWidth="2" />
    </svg>
  );
}

// ─── small shared corner ornament ────────────────────────────────────────
function CornerOrnament({ color, size = 56 }: { color: string; size?: number }) {
  return (
    <svg viewBox="0 0 60 60" style={{ width: size, height: size }} aria-hidden>
      <path d="M4 4 L 24 4 M4 4 L 4 24" stroke={color} strokeWidth="1.4" fill="none" />
      <path
        d="M16 16 L 20 6 L 24 16 L 32 12 L 28 20 L 36 24 L 28 28 L 32 36 L 24 32 L 20 40 L 16 32 L 8 36 L 12 28 L 4 24 L 12 20 Z"
        fill={color}
        opacity="0.85"
      />
    </svg>
  );
}

export function VintageFairVariant({ t, isDark, ctx }: VariantProps) {
  const { play } = useSound();
  const reduce = useReducedMotion();

  const crimson = t.accent;
  const cream = t.bg;
  const ink = t.ink;
  const heroPhoto = t.photos[0];

  // Derive numeric age from t.names ("Aziza 25 yosh" / "Aziza · 25" / "Aziza")
  // — falls back to the design's flagship 25 if no number present.
  const ageNumber = ((): string => {
    const m = t.names.match(/(\d{1,3})\s*(yosh|yil)?\s*$/i);
    return m ? m[1] : "25";
  })();

  // Mobile detection: disable spin on small screens for perf/reduced-motion safety
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(max-width: 640px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);

  const wheelSpins = !reduce && !isMobile;

  // ─── Hero ferris reveal — fire swoosh once ─────────────────────────────
  useEffect(() => {
    if (reduce) return;
    const id = window.setTimeout(() => {
      play("swoosh");
    }, 350);
    return () => window.clearTimeout(id);
  }, [reduce, play]);

  // ─── Ambient calming pad — D major nostalgic carousel ──────────────────
  const [music, setMusic] = useState(false);
  useAmbient(music, {
    freqs: [146.83, 220.0, 293.66, 369.99],
    type: "sine",
    master: 0.035,
    lfo: 0.14,
  });

  // ─── Schedule row hover style ──────────────────────────────────────────
  const scheduleHoverStyle: CSSProperties = {};

  return (
    <div className="v-b07">
      <style>{`
        @keyframes vfWheelSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        [data-section-schedule-row] {
          transition: background-color 200ms ease, transform 200ms ease;
        }
        [data-section-schedule-row]:hover {
          background-color: ${crimson}14;
          transform: translateX(2px);
        }
        .vf-rsvp-wrap button[data-rsvp="yes"],
        .vf-rsvp-wrap button[aria-pressed="true"] {
          box-shadow: 0 0 0 2px ${crimson}, 0 0 18px ${crimson}aa, 0 0 36px ${crimson}55 !important;
        }
        .v-b07 button:focus-visible,
        .v-b07 a:focus-visible {
          outline: 2px solid ${crimson};
          outline-offset: 3px;
        }
        @media (max-width: 768px) {
          .v-b07 .v-b07-hero {
            padding: 64px 18px 48px !important;
            min-height: auto !important;
          }
          .v-b07 .v-b07-hero-grid {
            grid-template-columns: 1fr !important;
            gap: 24px !important;
            padding: 16px 6px !important;
          }
          .v-b07 .v-b07-hero h1,
          .v-b07 .v-b07-hero-name {
            font-size: clamp(40px, 12vw, 84px) !important;
            line-height: 0.95 !important;
          }
          .v-b07 .v-b07-illus-stack {
            gap: 6px !important;
          }
          /* Ferris wheel container: cap to viewport width */
          .v-b07 .v-b07-ferris-wrap {
            max-width: min(300px, 82vw) !important;
            width: 100% !important;
          }
          /* Photo box in left column: cap height to avoid excessive scroll */
          .v-b07 .v-b07-photo-box {
            width: min(240px, 70vw) !important;
            aspect-ratio: 4/5 !important;
          }
          .v-b07 .v-b07-illus-stack > div:nth-child(3) {
            max-width: min(280px, 80vw) !important;
          }
          /* Tent + popcorn row: shrink proportionally */
          .v-b07 .v-b07-illus-pair { gap: 8px !important; }
          .v-b07 button,
          .v-b07 a[role="button"] {
            min-height: 44px;
          }
        }
      `}</style>

      {/* ── HERO — vintage poster broadside ───────────────────────────────── */}
      <section
        className="hero-fill v-b07-hero"
        style={{
          minHeight: "100vh",
          padding: "80px 24px 64px",
          background: cream,
          color: ink,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* paper grain overlay */}
        <svg
          aria-hidden
          preserveAspectRatio="none"
          viewBox="0 0 100 100"
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0.16, pointerEvents: "none" }}
        >
          <defs>
            <pattern id="vf-hero-grain" x="0" y="0" width="3" height="3" patternUnits="userSpaceOnUse">
              <circle cx="0.6" cy="0.6" r="0.25" fill={ink} />
              <circle cx="2.2" cy="1.6" r="0.18" fill={ink} />
            </pattern>
          </defs>
          <rect x="0" y="0" width="100" height="100" fill="url(#vf-hero-grain)" />
        </svg>

        {/* hairline frame */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 16,
            border: `1.6px solid ${ink}`,
            pointerEvents: "none",
          }}
        />
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 24,
            border: `0.7px solid ${crimson}`,
            pointerEvents: "none",
          }}
        />

        {/* corner ornaments */}
        <div style={{ position: "absolute", top: 22, left: 22, color: crimson }}>
          <CornerOrnament color={crimson} size={64} />
        </div>
        <div style={{ position: "absolute", top: 22, right: 22, color: crimson, transform: "scaleX(-1)" }}>
          <CornerOrnament color={crimson} size={64} />
        </div>
        <div style={{ position: "absolute", bottom: 22, left: 22, color: crimson, transform: "scaleY(-1)" }}>
          <CornerOrnament color={crimson} size={64} />
        </div>
        <div style={{ position: "absolute", bottom: 22, right: 22, color: crimson, transform: "scale(-1, -1)" }}>
          <CornerOrnament color={crimson} size={64} />
        </div>

        <div
          className="v-b07-hero-grid"
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            position: "relative",
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
            gap: 24,
            alignItems: "center",
            padding: "32px 16px",
          }}
        >
          {/* LEFT column — typographic stack */}
          <div style={{ position: "relative", textAlign: "center", paddingTop: 16 }}>
            <div
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: "0.5em",
                color: crimson,
                marginBottom: 4,
              }}
            >
              ★ HEAR YE · HEAR YE ★
            </div>
            <div
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: "0.4em",
                color: ink,
                marginBottom: 20,
              }}
            >
              THE GRAND COUNTY FAIR — PRESENTS
            </div>
            <div
              style={{
                fontFamily: t.font,
                fontWeight: 900,
                fontStyle: "italic",
                fontSize: "clamp(48px, 9vw, 96px)",
                lineHeight: 0.92,
                color: crimson,
                letterSpacing: "-0.01em",
                margin: 0,
                textShadow: `2px 2px 0 ${ink}`,
              }}
            >
              TOMOSHA
            </div>
            <div
              style={{
                marginTop: 6,
                fontFamily: "var(--font-ui)",
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: "0.45em",
                color: ink,
              }}
            >
              ── {ageNumber} YIL ──
            </div>

            {/* Aziza name */}
            <div
              className="v-b07-hero-name"
              style={{
                marginTop: 18,
                fontFamily: t.font,
                fontStyle: "italic",
                fontWeight: 400,
                fontSize: "clamp(56px, 11vw, 132px)",
                lineHeight: 0.95,
                color: ink,
              }}
            >
              {t.names.replace(/\s*[·•]?\s*\d+\s*(yosh|yil)?\s*$/i, "").split(/[·&]/)[0]?.trim() || t.names}
            </div>

            {/* photo in old-camera frame */}
            <div
              className="v-b07-photo-box"
              style={{
                margin: "26px auto 0",
                width: "min(280px, 70%)",
                aspectRatio: "4 / 5",
                position: "relative",
                background: ink,
                padding: 10,
                boxShadow: `6px 6px 0 -1px ${crimson}55`,
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: -10,
                  left: 12,
                  right: 12,
                  height: 14,
                  background: ink,
                  borderRadius: 2,
                }}
              />
              <div
                style={{
                  position: "absolute",
                  top: -16,
                  left: "44%",
                  width: 18,
                  height: 8,
                  background: ink,
                  borderRadius: 1,
                }}
              />
              <div
                style={{
                  position: "absolute",
                  inset: 10,
                  backgroundImage: heroPhoto ? `url("${heroPhoto}")` : undefined,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  background: heroPhoto ? undefined : `linear-gradient(160deg, ${crimson}33, ${cream})`,
                  filter: "sepia(0.55) saturate(0.85) contrast(0.95)",
                  display: "grid",
                  placeItems: "center",
                }}
              >
                {!heroPhoto && (
                  <div style={{ fontFamily: t.font, fontStyle: "italic", color: crimson, fontSize: 28 }}>A.</div>
                )}
              </div>
              <div
                aria-hidden
                style={{
                  position: "absolute",
                  inset: 10,
                  boxShadow: `inset 0 8px 14px ${ink}66, inset 0 -8px 14px ${ink}66`,
                  pointerEvents: "none",
                }}
              />
            </div>

            <div
              style={{
                marginTop: 22,
                fontFamily: t.font,
                fontStyle: "italic",
                fontSize: 28,
                color: ink,
                lineHeight: 1,
              }}
            >
              {t.date}
            </div>
            <div
              style={{
                marginTop: 4,
                fontFamily: "var(--font-ui)",
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: "0.4em",
                color: crimson,
              }}
            >
              {t.venue.toUpperCase()}
            </div>
            <div
              style={{
                marginTop: 18,
                fontFamily: "var(--font-ui)",
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: "0.5em",
                color: cream,
                background: crimson,
                padding: "8px 16px",
                display: "inline-block",
                transform: "rotate(-2deg)",
              }}
            >
              ★ STEP RIGHT UP! ★
            </div>
          </div>

          {/* RIGHT column — illustrations stacked */}
          <div className="v-b07-illus-stack" style={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
            <div
              style={{
                fontFamily: t.font,
                fontStyle: "italic",
                fontSize: "clamp(28px, 4vw, 40px)",
                color: crimson,
                textAlign: "center",
                lineHeight: 1,
              }}
            >
              FAIR OPENS!
            </div>
            <div
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: 9,
                fontWeight: 800,
                letterSpacing: "0.4em",
                color: ink,
                textAlign: "center",
              }}
            >
              ── ANNO 1923 · MMXXVI ──
            </div>

            <div className="v-b07-ferris-wrap" style={{ width: "100%", maxWidth: 360, aspectRatio: "1 / 1", position: "relative", marginTop: 8 }}>
              <FerrisWheel crimson={crimson} ink={ink} bg={cream} spin={wheelSpins} size={360} />
            </div>

            <div className="v-b07-illus-pair" style={{ display: "flex", alignItems: "flex-end", justifyContent: "center", gap: 16, marginTop: 8, width: "100%" }}>
              <StripedTent crimson={crimson} ink={ink} bg={cream} size={170} />
              <PopcornCart crimson={crimson} ink={ink} bg={cream} size={130} />
            </div>

            <div
              style={{
                marginTop: 14,
                fontFamily: t.font,
                fontStyle: "italic",
                fontSize: "clamp(20px, 3vw, 28px)",
                color: ink,
                textAlign: "center",
                lineHeight: 1.2,
                background: cream,
                border: `1.4px solid ${ink}`,
                padding: "10px 20px",
                boxShadow: `4px 4px 0 -1px ${crimson}`,
                transform: "rotate(1.5deg)",
              }}
            >
              “Bilet bir, xotira ming!”
            </div>
          </div>
        </div>
      </section>

      {/* ── COUNTDOWN ─────────────────────────────────────────────────────── */}
      <Reveal>
        <CountdownSection t={t} {...ctx.countdown} isDark={isDark} />
      </Reveal>

      {/* ── STORY (trimmed) ───────────────────────────────────────────────── */}
      <Reveal>
        <StorySection
          t={t}
          eyebrow="Tomosha haqida"
          title="Yangiobod parkida o'sgan qiz"
          body={`Aziza shu kuzda ${ageNumber} yoshga to'ladi. Bolaligida har yakshanba dadasi uni Yangiobod parkiga olib kelar edi — ferris-wheel, popkorn aravasi, kalliope organ. Bugun u o'sha tomoshani eng aziz do'stlari uchun qaytarib beradi: bilet bir, xotira ming.`}
        />
      </Reveal>

      {/* ── GALLERY ───────────────────────────────────────────────────────── */}
      <Reveal>
        <GallerySection t={t} />
      </Reveal>

      {/* ── SCHEDULE ─────────────────────────────────────────────────────── */}
      <Reveal>
        <div
          onClickCapture={(e) => {
            const target = e.target as HTMLElement | null;
            if (!target) return;
            if (target.closest("button")) return; // ignore the ICS button
            play("tap");
          }}
          style={scheduleHoverStyle}
        >
          <ScheduleSection t={t} items={buildSchedule(ageNumber)} />
        </div>
      </Reveal>

      {/* ── MAP ─────────────────────────────────────────────────────────── */}
      <Reveal>
        <MapSection t={t} isDark={isDark} addressLine="Yangiobod Park, Yashnobod tumani, Toshkent shahri" />
      </Reveal>

      {/* ── RSVP (with chime + glow halo on YES) ─────────────────────────── */}
      <Reveal>
        <div
          className="vf-rsvp-wrap"
          onClickCapture={(e) => {
            const target = e.target as HTMLElement | null;
            if (!target) return;
            const btn = target.closest("button");
            if (btn && /yes|ha|✓|kelaman|marhamat|tomoshaga/i.test(btn.textContent || "")) {
              play("chime");
            }
          }}
        >
          <RsvpSection
            t={t}
            isDark={isDark}
            {...ctx.rsvp}
            slug={ctx.slug}
            label="Tomoshaga keling, marhamat"
          />
        </div>
      </Reveal>

      {/* ── SHARE ─────────────────────────────────────────────────────────── */}
      <Reveal>
        <ShareSection t={t} />
      </Reveal>

      {/* ── FOOTER ────────────────────────────────────────────────────────── */}
      <GuestFooter t={t} />

      {/* ── Floating ambient music toggle ─────────────────────────────────── */}
      <MusicToggle
        on={music}
        onToggle={() => setMusic((m) => !m)}
        accent={t.accent}
        ink={t.bg}
        style={{ bottom: 18, right: 18 }}
      />
    </div>
  );
}
